import { AMBIENT, mountAmbientBackground } from '../../engine/ambient-background';
import { GameTab } from '../../engine/tabs';
import { showToast } from '../../engine/toast';
import { setTabBadge } from '../../engine/tab-badges';
import { formatMarchTime } from './march-format';
import {
  cancelBattle,
  fetchBattleHistory,
  fetchFormations,
  fetchMyColonyId,
  type FormationDef,
  type FormationId,
  type BattleHistoryEntry,
  type BattleIntel,
  type BattleResultSummary,
  type ResourceType,
  type UnitType,
} from './battle-api';
import { fetchNotifications, markNotificationsRead, type AttackIncomingPayload, type GameNotification } from '../notifications/notifications-api';
import { icon, type IconName } from '../../engine/icons';
import { unitArtFor } from '../../engine/unit-art';
import { formatCeil, formatShort, sumCeil } from '../../engine/format-short';
import { resourceIcon } from '../../engine/resource-icon';
import { buildingIconFor } from '../../engine/building-art';
import { motionAllowedForGlitch } from '../../engine/glitch';
import { t, TABLES, type Translations } from '../../engine/i18n/i18n';

/**
 * Battle tab: pure history view. Under the march-then-resolve model, the
 * attack affordance itself lives entirely on the World Map (tap an enemy
 * colony plot -> Send Army -> send-army-modal.ts) — this screen never
 * initiates anything, it only shows what already happened or is still
 * marching. See battle-scene.ts for the old instant-resolve canvas playback,
 * left in place but unused (nothing here calls it anymore).
 */

/** How often the pending-battle countdowns tick, purely UI-local (not a game-logic loop — see CLAUDE.md's lazy-eval rule, which this doesn't violate: nothing here computes state, it only re-renders a countdown to a timestamp the server already gave us). */
const COUNTDOWN_TICK_MS = 1_000;
/** Minimum time between auto-refetches triggered by a countdown crossing zero, so server/client clock skew (countdown says arrived, server still says PENDING) can't turn into a request-per-tick loop. */
const REFETCH_COOLDOWN_MS = 5_000;

/** Mirrors backend battle.constants.ts's MARCH_CANCEL_CUTOFF_FRACTION — display-only; the server is the sole authority on whether a cancel actually succeeds. */
const MARCH_CANCEL_CUTOFF_FRACTION = 0.9;

/** Pure: whether a march can still be cancelled at `now`, given its start/arrival times — same formula as backend's canCancelMarch. */
export function canCancelMarch(startedAt: string, arrivalAt: string, now: number = Date.now()): boolean {
  const total = new Date(arrivalAt).getTime() - new Date(startedAt).getTime();
  if (total <= 0) return false;
  const elapsed = now - new Date(startedAt).getTime();
  return elapsed / total <= MARCH_CANCEL_CUTOFF_FRACTION;
}

/** Look up a glossary term by dynamic enum value; undefined when the glossary has no entry (new backend enum value). */
function termLabel(prefix: 'resource' | 'unit' | 'building', type: string): string | undefined {
  const key = `term.${prefix}.${type}`;
  return key in TABLES.en ? t(key as keyof Translations) : undefined;
}

function resourceLabel(type: ResourceType | string): string {
  return termLabel('resource', type) ?? type;
}

/** Unit name from the shared glossary; unknown types fall back to a prettified enum value. */
function formatUnitLabel(unitType: UnitType | string): string {
  return termLabel('unit', unitType) ?? prettifyEnum(unitType);
}

function formatBuildingLabel(buildingType: string): string {
  return termLabel('building', buildingType) ?? prettifyEnum(buildingType);
}

function prettifyEnum(unitType: string): string {
  return unitType
    .toLowerCase()
    .split('_')
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(' ');
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

/** Defender's colony can be null in rare edge cases (e.g. its plot record vanished); fall back to plain text rather than rendering "(undefined, undefined)". */
function formatPosition(position: { x: number; y: number } | null): string {
  return position ? `(${position.x}, ${position.y})` : t('battle.unknownLocation');
}


function formatTiles(n: number): string {
  return t(n === 1 ? 'battle.tiles.one' : 'battle.tiles.other', { n });
}

/** "(x, y) · N tiles · 4m 12s march" — one translatable template so word order can differ per language. */
function formatRoute(row: BattleHistoryEntry): string {
  return t('battle.route', {
    position: formatPosition(row.defenderPosition),
    distance: formatTiles(row.distanceTiles),
    march: t('battle.marchTime', { time: formatMarchTime(row.marchSeconds) }),
  });
}

function formatCountdown(remainingMs: number): string {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return hours > 0 ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`;
}

/**
 * Which side of a row the viewer was on. A pure, exported, directly-testable
 * function since `attackerId`/`defenderId`'s exact semantics (colony id vs
 * user id) are not yet confirmed against the live backend — isolating the
 * comparison here means that, if it turns out to be a different field
 * entirely, only this function (and its unit tests) need to change, not the
 * render path built on top of it. Defaults to 'attacker' if neither side
 * matches (shouldn't happen — the endpoint only returns the caller's own
 * battles — but a render must pick something rather than throw).
 */
export function viewerSide(row: BattleHistoryEntry, myColonyId: string | null): 'attacker' | 'defender' {
  if (myColonyId && row.defenderId === myColonyId && row.attackerId !== myColonyId) return 'defender';
  return 'attacker';
}

export interface ResolvedFraming {
  /** The viewer's own losses, by unit type. */
  yourLosses: Partial<Record<UnitType, number>>;
  /**
   * The viewer's own post-battle surviving garrison, by unit type — only
   * non-null when the viewer WAS the defender (`defenderRemaining` is only
   * ever about the defender's garrison, never the attacker's, since the
   * attacker's post-battle roster isn't tracked in the result at all).
   */
  yourRemaining: Partial<Record<UnitType, number>> | null;
  /** The opposing side's losses, by unit type (what the viewer killed). */
  kills: Partial<Record<UnitType, number>>;
  /**
   * The enemy's post-battle surviving garrison, by unit type — only
   * non-null when the viewer WAS the attacker, i.e. `kills` above is sourced
   * from `defenderLosses` (same unit-type set `defenderRemaining` covers).
   */
  killsRemaining: Partial<Record<UnitType, number>> | null;
  /** Plunder shown as a gain (attacker + attacker won), a loss (defender + attacker won), or null (attacker lost — nothing was plundered). */
  plunder: { record: Partial<Record<ResourceType, number>>; sign: '+' | '-' } | null;
  won: boolean;
}

/** Pure framing of a resolved row from the viewer's perspective — see the four cases exercised in battle-screen.test.ts. */
export function frameResolved(summary: BattleResultSummary, side: 'attacker' | 'defender'): ResolvedFraming {
  const isAttacker = side === 'attacker';
  const won = isAttacker ? summary.attackerWins : !summary.attackerWins;
  const yourLosses = isAttacker ? summary.attackerLosses : summary.defenderLosses;
  const yourRemaining = isAttacker ? null : summary.defenderRemaining;
  const kills = isAttacker ? summary.defenderLosses : summary.attackerLosses;
  const killsRemaining = isAttacker ? summary.defenderRemaining : null;
  let plunder: ResolvedFraming['plunder'] = null;
  if (summary.attackerWins) {
    plunder = { record: summary.plunder, sign: isAttacker ? '+' : '-' };
  }
  return { yourLosses, yourRemaining, kills, killsRemaining, plunder, won };
}

/** Sum of every value in a quantity record — used for the collapsed row's at-a-glance totals (deaths/kills/resources), where the per-type breakdown only shows once expanded. */
function sumQty(record: Partial<Record<string, number>>): number {
  return Object.values(record).reduce<number>((sum, v) => sum + (v ?? 0), 0);
}

interface UnitTableRow {
  label: string;
  qty: number;
  remaining: number | null;
}

/** Rows for a unit-count table, sorted by qty descending (biggest losses/kills first) and filtering out zero-qty entries — a unit type touched by the fight but with 0 actually lost/killed isn't worth a row. `remaining`, when given, only supplies a value for unit types present in `losses` (by construction, both records always share the same key set — see ResolvedFraming's doc comments). */
function unitTableRows(
  losses: Partial<Record<string, number>>,
  remaining: Partial<Record<string, number>> | null,
): UnitTableRow[] {
  return Object.entries(losses)
    .filter((e): e is [string, number] => (e[1] ?? 0) > 0)
    .map(([key, qty]) => ({ label: formatUnitLabel(key), qty, remaining: remaining ? (remaining[key] ?? 0) : null }))
    .sort((a, b) => b.qty - a.qty);
}

/** A small 2- or 3-column table (unit name, qty, optional remaining-garrison), or a plain "None" line when there's nothing to show. `remainingHeader` is only used (and only rendered as a column) when at least one row actually carries a remaining count. */
function renderUnitTable(title: string, qtyHeader: string, remainingHeader: string, rows: UnitTableRow[]): string {
  if (rows.length === 0) {
    return `<div class="text-xs"><span class="text-text-dim">${title}:</span> <span class="text-text">${t('battle.table.none')}</span></div>`;
  }
  const showRemaining = rows.some((r) => r.remaining !== null);
  return `
    <div class="flex flex-col gap-1">
      <span class="text-text-dim text-[11px]">${title}</span>
      <table class="w-full text-[11px] border-collapse">
        <thead>
          <tr class="text-text-dim">
            <th class="text-left font-normal py-0.5">${t('battle.table.unit')}</th>
            <th class="text-right font-normal py-0.5">${qtyHeader}</th>
            ${showRemaining ? `<th class="text-right font-normal py-0.5">${remainingHeader}</th>` : ''}
          </tr>
        </thead>
        <tbody>
          ${rows
            .map(
              (r) => `
            <tr class="border-t border-border/50">
              <td class="py-0.5 text-text">${r.label}</td>
              <td class="py-0.5 text-right text-text tabular-nums">${r.qty}</td>
              ${showRemaining ? `<td class="py-0.5 text-right text-text-dim tabular-nums">${r.remaining ?? 0}</td>` : ''}
            </tr>`,
            )
            .join('')}
        </tbody>
      </table>
    </div>
  `;
}

function intelTierLabel(tier: BattleIntel['tier']): string {
  return t(`battle.intel.${tier}` as keyof Translations);
}

/** The four things a scouting mission can reveal, in the order the intel tiers unlock them. */
const INTEL_STEPS: { icon: IconName; label: keyof Translations; from: number }[] = [
  { icon: 'hammer', label: 'battle.intel.step.buildings', from: 1 },
  { icon: 'troops', label: 'battle.intel.step.garrison', from: 2 },
  { icon: 'crosshair', label: 'battle.intel.step.types', from: 3 },
  { icon: 'crate', label: 'battle.intel.step.resources', from: 4 },
];
const INTEL_LEVEL: Record<BattleIntel['tier'], number> = { NONE: 0, BUILDINGS: 1, UNIT_COUNTS: 2, UNIT_COUNTS_AND_TYPES: 3, FULL: 4 };

/** The intel "meter": four icon tiles, lit for what this mission revealed and dim for what it did not — readable at a glance. */
function intelMeterHtml(tier: BattleIntel['tier']): string {
  const level = INTEL_LEVEL[tier];
  return `<span class="bl-meter" role="img" aria-label="${intelTierLabel(tier)}" title="${intelTierLabel(tier)}">${INTEL_STEPS.map(
    (st) => `<span class="bl-meter-step${level >= st.from ? ' bl-meter-step--on' : ''}" title="${t(st.label)}">${icon(st.icon)}</span>`,
  ).join('')}</span>`;
}

function intelCard(iconName: IconName, label: string, inner: string, modifier = ''): string {
  return `<section class="bl-intel-card${modifier}"><div class="bl-intel-head"><span aria-hidden="true">${icon(iconName)}</span><span class="bl-intel-title">${label}</span></div>${inner}</section>`;
}

/** Intel block — shared by a recon mission's report and the side-benefit intel an ordinary assault can earn. One card per topic; each building / unit is a labelled tile. */
function renderIntel(intel: BattleIntel | undefined): string {
  if (!intel || intel.tier === 'NONE') return '';
  const cards: string[] = [];

  if (intel.buildings && intel.buildings.length > 0) {
    cards.push(
      intelCard(
        'hammer',
        t('battle.intel.step.buildings'),
        `<div class="bl-itiles">${intel.buildings
          .map((b) => {
            const iconSrc = buildingIconFor(b.type as never, b.level);
            const name = formatBuildingLabel(b.type);
            return `<div class="bl-itile bl-itile--building" title="${name} — ${t('battle.level', { level: b.level })}">
              <span class="bl-itile-img">${iconSrc ? `<img src="${iconSrc}" alt="" loading="lazy" />` : `<span class="bl-itile-ph">${icon('hammer')}</span>`}</span>
              <span class="bl-itile-name">${name}</span>
              <span class="bl-itile-lvl">${t('battle.level', { level: b.level })}</span>
            </div>`;
          })
          .join('')}</div>`,
      ),
    );
  }

  // Units: always a card once the garrison step is in play, so the player is told when a breakdown was NOT retrieved.
  const garrisonTotal =
    intel.totalUnitCount ?? (intel.unitCounts ? Object.values(intel.unitCounts).reduce<number>((a, v) => a + (v ?? 0), 0) : undefined);
  const unitRows = intel.unitCounts ? Object.entries(intel.unitCounts).filter((e): e is [string, number] => (e[1] ?? 0) > 0) : [];
  const unitTiles = unitRows
    .map(([type, q]) => {
      const art = unitArtFor(type as UnitType);
      const name = formatUnitLabel(type);
      return `<div class="bl-itile bl-itile--unit" title="${name}: ${q}">
        <span class="bl-itile-img">${art ? `<img src="${art.thumb}" alt="" loading="lazy" />` : `<span class="bl-itile-ph">${icon('crosshair')}</span>`}</span>
        <span class="bl-itile-name">${name}</span>
        <span class="bl-itile-lvl bl-itile-qty">${t('battle.qty', { n: formatShort(q) })}</span>
      </div>`;
    })
    .join('');
  const noBreakdown = `<p class="bl-intel-none" data-intel-no-units>${t('battle.intel.noUnits')}</p>`;
  // Two columns, roughly 1:5: the head-count on the left, the per-unit breakdown on the right. When there is no
  // breakdown the notice sits centred in the right column.
  const countLabel = garrisonTotal !== undefined ? t(garrisonTotal === 1 ? 'battle.garrison.one' : 'battle.garrison.other', { n: garrisonTotal }) : '';
  const unitsInner = `<div class="bl-garrison">
    <div class="bl-garrison-count" ${countLabel ? `title="${countLabel}"` : ''}>
      <span class="bl-big">${garrisonTotal !== undefined ? formatShort(garrisonTotal) : '?'}</span>
      <span class="bl-big-unit">${t(garrisonTotal === 1 ? 'battle.units.one' : 'battle.units.other')}</span>
    </div>
    <div class="bl-garrison-break">${unitRows.length > 0 ? `<div class="bl-itiles">${unitTiles}</div>` : noBreakdown}</div>
  </div>`;
  cards.push(intelCard('troops', t('battle.intel.step.garrison'), unitsInner));

  if (intel.resources) {
    const rows = Object.entries(intel.resources).filter((e): e is [string, number] => (e[1] ?? 0) > 0);
    if (rows.length > 0) {
      cards.push(
        intelCard(
          'crate',
          t('battle.intel.step.resources'),
          `<div class="bl-itiles">${rows
            .map(
              ([type, a]) =>
                `<div class="bl-itile bl-itile--res" title="${resourceLabel(type)}: ${formatCeil(a)}"><span class="bl-itile-img"><span class="bl-itile-ph">${resourceIcon(type, 1.6) || icon('crate')}</span></span><span class="bl-itile-name">${resourceLabel(type)}</span><span class="bl-itile-lvl bl-itile-qty">${formatShort(a)}</span></div>`,
            )
            .join('')}</div>`,
        ),
      );
    }
  }
  return `<div class="bl-intel"><div class="bl-intel-top">${intelMeterHtml(intel.tier)}<span class="sr-only">${intelTierLabel(intel.tier)}</span></div><div class="bl-intel-cards">${cards.join('')}</div></div>`;
}

/**
 * The three glance tiles for a scouting mission, laid out exactly like any other battle's: Deaths (your recon units
 * lost), Kills (none — a scout does not fight) and, where Resources would be, a blue-bordered "Recon mission" tile
 * saying how much of the target was revealed (steps out of four).
 */
function reconTilesHtml(summary: BattleResultSummary, side: 'attacker' | 'defender'): string {
  const deaths = side === 'attacker' ? sumQty(summary.attackerLosses) : 0;
  const level = INTEL_LEVEL[summary.intel?.tier ?? 'NONE'];
  return `
    <div class="bl-tiles" data-recon-tiles>
      <div class="bl-tile ${deaths > 0 ? 'bl-tile--loss' : 'bl-tile--none'}">
        <span class="bl-tile-ico">${icon('skull')}</span>
        <span class="bl-tile-label">${t('battle.stat.deaths')}</span>
        <span class="bl-tile-n tabular-nums" data-count="${deaths}">${fmtInt(deaths)}</span>
      </div>
      <div class="bl-tile bl-tile--none">
        <span class="bl-tile-ico">${icon('battle')}</span>
        <span class="bl-tile-label">${t('battle.stat.kills')}</span>
        <span class="bl-tile-n tabular-nums">${t('battle.table.none')}</span>
      </div>
      <div class="bl-tile bl-tile--recon" data-recon-tile>
        <span class="bl-tile-ico">${icon('map')}</span>
        <span class="bl-tile-label">${t('battle.stat.recon')}</span>
        <span class="bl-tile-n tabular-nums">${level}/${INTEL_STEPS.length}</span>
      </div>
    </div>
  `;
}

/**
 * The resources table — an absolute fact (what the attacker actually took),
 * not framed relative to the viewer, so it reads the same whether you were
 * the attacker or the defender. Empty (nothing plundered, or the attacker
 * lost) renders the same "None" placeholder `renderUnitTable` uses, so all
 * three battle-log columns share one empty-state look.
 */
function renderResourceTable(title: string, plunder: Partial<Record<ResourceType, number>>, tone: 'gain' | 'loss' = 'gain'): string {
  const entries = Object.entries(plunder).filter((e): e is [string, number] => (e[1] ?? 0) > 0);
  if (entries.length === 0) {
    return `<div class="text-xs"><span class="text-text-dim">${title}:</span> <span class="text-text">${t('battle.table.none')}</span></div>`;
  }
  return `
    <div class="flex flex-col gap-1">
      <div class="bl-loot-art" style="background-image:url('${BANNER_ART.loot}')" aria-hidden="true"></div>
      <span class="text-text-dim text-[11px]">${title}</span>
      <table class="w-full text-[11px] border-collapse">
        <thead>
          <tr class="text-text-dim">
            <th class="text-left font-normal py-0.5">${t('battle.table.resource')}</th>
            <th class="text-right font-normal py-0.5">${t('battle.table.amount')}</th>
          </tr>
        </thead>
        <tbody>
          ${entries
            .map(
              ([res, amt]) => `
            <tr class="border-t border-border/50">
              <td class="py-0.5 text-text">${resourceIcon(res)} ${resourceLabel(res)}</td>
              <td class="py-0.5 text-right ${tone === 'loss' ? 'text-crimson' : 'text-success'} tabular-nums">${tone === 'loss' ? '\u2212' : ''}${formatCeil(amt)}</td>
            </tr>`,
            )
            .join('')}
        </tbody>
      </table>
    </div>
  `;
}

/**
 * The battle log's expanded detail, laid out as three columns — attacker
 * losses, defender losses, resources pillaged — one request item apiece.
 * Absolute (sourced straight from the summary, not framed per viewer side)
 * so the same three columns render identically whichever side you were on;
 * only the collapsed row's Deaths/Kills/Resources glance (statColumnsHtml)
 * stays viewer-relative. Stacks to one column on narrow viewports.
 */
function renderBattleColumns(summary: BattleResultSummary, side: 'attacker' | 'defender' = 'attacker'): string {
  const plunder = summary.attackerWins ? summary.plunder : {};
  if (side === 'defender') {
    // The defender learns what hit them and what it cost — the army that came, their own losses, what was taken —
    // and nothing about the enemy's own side beyond that (no intel, no garrison breakdown).
    const sent = summary.attackerSent;
    const armyRows = sent
      ? unitTableRows(sent, summary.attackerLosses)
      : unitTableRows(summary.attackerLosses, null);
    return `
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-3" data-defender-report>
      ${sent
        ? renderUnitTable(t('battle.attackingArmy'), t('battle.table.sent'), t('battle.table.lost'), armyRows)
        : renderUnitTable(t('battle.attackingArmy'), t('battle.table.lost'), t('battle.table.remaining'), armyRows)}
      ${renderUnitTable(t('battle.yourLosses'), t('battle.table.lost'), t('battle.table.remaining'), unitTableRows(summary.defenderLosses, null))}
      ${renderResourceTable(t('battle.resourcesStolen'), plunder, 'loss')}
    </div>
  `;
  }
  const attackerRows = unitTableRows(summary.attackerLosses, null);
  const defenderRows = unitTableRows(summary.defenderLosses, summary.defenderRemaining);
  return `
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
      ${renderUnitTable(t('battle.attackerLosses'), t('battle.table.lost'), t('battle.table.remaining'), attackerRows)}
      ${renderUnitTable(t('battle.defenderLosses'), t('battle.table.lost'), t('battle.table.remaining'), defenderRows)}
      ${renderResourceTable(t('battle.resourcesPillaged'), plunder)}
    </div>
  `;
}


// ─────────────────────────────────────────────── gamified log: art, buffs, count-up

/** Banner art per verdict, generated for the log (public/battle). */
const BANNER_ART = {
  win: '/battle/victory.webp',
  loss: '/battle/defeat.webp',
  recon: '/battle/recon.webp',
  marching: '/battle/marching.webp',
  incoming: '/battle/incoming.webp',
  loot: '/battle/loot.webp',
} as const;

/** "+10%" / "\u221220%" from a multiplier; '' when it changes nothing. */
function signedPct(factor: number): string {
  const pct = Math.round((factor - 1) * 100);
  if (pct === 0) return '';
  return `${pct > 0 ? '+' : '\u2212'}${Math.abs(pct)}%`;
}

/** A formation bonus in its own colour, in parentheses — always distinct from the green/red of results. */
function buffHtml(text: string): string {
  return text ? ` <span class="bl-buff">(${text})</span>` : '';
}

function formationName(id: FormationId): string {
  return t(`worldmap.formation.${id}.name` as keyof Translations);
}

/** Whole number with thousands separators, matching the rest of the UI. */
function fmtInt(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

/**
 * Counts the `[data-count]` numbers under `root` up from zero. Skipped where the browser cannot animate (jsdom),
 * under reduced motion, and for anything already on screen: the final text is always in the markup, so a skipped
 * animation just means the number is already right.
 */
function animateCounts(root: Element): void {
  if (!motionAllowedForGlitch() || typeof (root as HTMLElement).getAnimations !== 'function') return;
  root.querySelectorAll<HTMLElement>('[data-count]').forEach((el) => {
    const target = Number(el.dataset.count);
    if (!Number.isFinite(target) || target <= 0) return;
    const prefix = el.dataset.prefix ?? '';
    const start = performance.now();
    const dur = 650 + Math.min(500, target * 2);
    const step = (now: number) => {
      const k = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - k, 3);
      el.textContent = `${prefix}${fmtInt(target * eased)}`;
      if (k < 1 && el.isConnected) requestAnimationFrame(step);
    };
    el.textContent = `${prefix}0`;
    requestAnimationFrame(step);
  });
}

/**
 * "Olympus Landing (DustRunner · Dust Walker)" — colony name, then the owner
 * and their worn title in one parenthetical. Replaces six copies of the same
 * inline expression that previously differed only in which side they read.
 * Each part is optional: a barbarian camp has no owner, and most commanders
 * wear no title.
 */
export function combatantLabel(
  colonyName: string,
  username: string | null | undefined,
  title: string | null | undefined,
): string {
  const who = [username, title].filter(Boolean).join(' \u00b7 ');
  return who ? `${colonyName} (${who})` : colonyName;
}

export function renderBattleScreen(root: HTMLElement): () => void {
  let disposed = false;
  let myColonyId: string | null = null;
  let history: BattleHistoryEntry[] = [];
  let countdownTimer: ReturnType<typeof setInterval> | null = null;
  let lastRefetchAt = 0;
  let refetchInFlight = false;
  /** Which RESOLVED rows are expanded to their full detail — collapsed by default, including for a row that just arrived (a battle resolving while the tab is open doesn't auto-expand it). */
  const expandedIds = new Set<string>();
  /** Rows already shown once: only a NEW row plays its entrance + count-up, so a re-render never replays them. */
  const seenRows = new Set<string>();
  /** Server formation table (name -> modifiers), for the buff parentheses. Empty = no parentheses, just names. */
  const formations = new Map<FormationId, FormationDef>();

  // The ambient layer is absolutely positioned, so the mount point has to be
  // the containing block.
  root.classList.add('relative', 'block', 'w-full', 'h-full', 'overflow-y-auto');

  root.innerHTML = `
    <div data-ambient class="absolute inset-0 overflow-hidden pointer-events-none"></div>
    <div class="relative p-4 sm:p-6 max-w-4xl mx-auto flex flex-col gap-4 pb-10">
      <h1 class="font-heading text-gold text-xl">${t('battle.title')}</h1>
      <p class="text-text-dim text-xs -mt-2">${t('battle.subtitle')}</p>
      <section id="battle-alerts-section" class="hidden bg-surface border border-crimson/50 rounded-lg p-4">
        <div class="flex items-center justify-between mb-2">
          <h2 class="font-heading text-crimson text-sm">${t('battle.alerts')}</h2>
          <button type="button" data-action="mark-all-read" class="text-[11px] text-text-dim hover:text-text underline min-h-11 px-2">${t('battle.markAllRead')}</button>
        </div>
        <div id="battle-alerts-list" class="flex flex-col gap-2 text-sm"></div>
      </section>
      <section class="bg-surface border border-border rounded-lg p-4">
        <div id="battle-history-list" class="flex flex-col gap-2 text-sm"></div>
      </section>
    </div>
  `;
  mountAmbientBackground(root.querySelector('[data-ambient]') as HTMLElement, AMBIENT.battle);

  const historyListEl = root.querySelector('#battle-history-list') as HTMLDivElement;
  const alertsSectionEl = root.querySelector('#battle-alerts-section') as HTMLElement;
  const alertsListEl = root.querySelector('#battle-alerts-list') as HTMLDivElement;
  let alerts: GameNotification[] = [];

  const enterClass = (id: string): string => (seenRows.has(id) ? '' : ' bl-enter');
  const rowStyle = (i: number): string => `style="--i:${Math.min(i, 12)}"`;
  const bannerStyle = (art: string): string => `style="background-image:url('${art}')"`;

  /** The formation chip for a row (nothing for Balanced / unknown). */
  function formationChipHtml(id: FormationId | undefined): string {
    if (!id || id === 'BALANCED') return '';
    return `<span class="bl-chip" data-battle-formation="${id}">${formationName(id)}</span>`;
  }

  /** Marching: banner, a progress bar that fills as the army nears, the countdown, and the recall button for the attacker. */
  function rowPendingHtml(row: BattleHistoryEntry, i: number): string {
    const remaining = new Date(row.arrivalAt).getTime() - Date.now();
    const side = viewerSide(row, myColonyId);
    const other = side === 'attacker'
      ? combatantLabel(row.defenderColonyName, row.defenderUsername, row.defenderTitle)
      : combatantLabel(row.attackerColonyName, row.attackerUsername, row.attackerTitle);
    const headline = t(side === 'attacker' ? 'battle.marchingTo' : 'battle.incomingFrom', { name: other });
    // Only the attacker can recall their own march, and only before the last
    // 10% of travel time — see canCancelMarch/MARCH_CANCEL_CUTOFF_FRACTION.
    const cancellable = side === 'attacker' && canCancelMarch(row.startedAt, row.arrivalAt);
    return `
      <div class="bl-card bl-card--march${enterClass(row.id)}" ${rowStyle(i)} data-battle-row="${row.id}" data-battle-status="PENDING">
        <div class="bl-art" ${bannerStyle(BANNER_ART.marching)} aria-hidden="true"></div>
        <div class="bl-body">
          <div class="flex items-center justify-between gap-2 flex-wrap">
            <span class="bl-title">${headline} ${side === 'attacker' ? formationChipHtml(row.formation) : ''}</span>
            <span class="bl-clock tabular-nums" data-battle-countdown="${row.id}">${formatCountdown(remaining)}</span>
          </div>
          <div class="bl-progress" aria-hidden="true"><span data-battle-progress="${row.id}" style="width:${marchProgressPct(row)}%"></span></div>
          <div class="bl-sub">${formatRoute(row)} &middot; ${formatDate(row.startedAt)}</div>
          ${side === 'attacker'
            ? `<button type="button" data-action="cancel-battle" data-battle-id="${row.id}" ${cancellable ? '' : 'disabled hidden'}
                 class="bl-cancel touch-manipulation">
                 <span class="bl-cancel-ico" aria-hidden="true">${icon('close')}</span><span>${t('battle.cancelAttack')}</span>
               </button>`
            : ''}
        </div>
      </div>
    `;
  }

  /** How far along a march is, 0-100, from the timestamps the server gave. */
  function marchProgressPct(row: BattleHistoryEntry): number {
    const start = new Date(row.startedAt).getTime();
    const end = new Date(row.arrivalAt).getTime();
    if (!(end > start)) return 100;
    return Math.max(0, Math.min(100, Math.round(((Date.now() - start) / (end - start)) * 100)));
  }

  /** The three glance tiles. Resources carries the formation's loot buff in parentheses when it applies. */
  function statColumnsHtml(framing: ResolvedFraming, summary: BattleResultSummary, side: 'attacker' | 'defender'): string {
    const deaths = sumQty(framing.yourLosses);
    const kills = sumQty(framing.kills);
    const resourceTotal = framing.plunder ? sumCeil(framing.plunder.record) : 0;
    const hasLoot = !!framing.plunder && resourceTotal > 0;
    const resourceColor = hasLoot ? (framing.plunder!.sign === '+' ? 'bl-tile--gain' : 'bl-tile--loss') : 'bl-tile--none';
    const sign = framing.plunder?.sign ?? '+';
    const resourceText = hasLoot ? `${sign}${fmtInt(resourceTotal)}` : '—';
    return `
      <div class="bl-tiles">
        <div class="bl-tile bl-tile--loss">
          <span class="bl-tile-ico">${icon('skull')}</span>
          <span class="bl-tile-label">${t('battle.stat.deaths')}</span>
          <span class="bl-tile-n tabular-nums" data-count="${deaths}">${fmtInt(deaths)}</span>
        </div>
        <div class="bl-tile bl-tile--gain">
          <span class="bl-tile-ico">${icon('battle')}</span>
          <span class="bl-tile-label">${t('battle.stat.kills')}</span>
          <span class="bl-tile-n tabular-nums" data-count="${kills}">${fmtInt(kills)}</span>
        </div>
        <div class="bl-tile ${resourceColor}">
          <span class="bl-tile-ico">${icon('crate')}</span>
          <span class="bl-tile-label">${t('battle.stat.resources')}</span>
          <span class="bl-tile-n tabular-nums" ${hasLoot ? `data-count="${resourceTotal}" data-prefix="${sign}"` : ''}>${resourceText}</span>
          ${hasLoot && side === 'attacker' ? `<span class="bl-tile-buff">${lootBuffHtml(summary)}</span>` : ''}
        </div>
      </div>
    `;
  }

  /** The formation's effect on the haul: the carry bonus when carry capped it, otherwise the loot-share bonus. */
  function lootBuffHtml(summary: BattleResultSummary): string {
    const f = summary.formation ? formations.get(summary.formation) : undefined;
    const base = formations.get('BALANCED');
    if (!f || !base) return '';
    if (summary.plunderCapped) return buffHtml(f.carryFactor !== 1 ? `${signedPct(f.carryFactor)} ${t('battle.buff.carry')}` : '').trim();
    return buffHtml(f.lootShare !== base.lootShare ? `${signedPct(f.lootShare / base.lootShare)} ${t('battle.buff.loot')}` : '').trim();
  }

  /** The attacker's own numbers with the formation's bonuses in parentheses: power, march time, haul capacity. */
  function formationStripHtml(row: BattleHistoryEntry, summary: BattleResultSummary, side: 'attacker' | 'defender'): string {
    if (side !== 'attacker') return '';
    const id = summary.formation ?? row.formation ?? 'BALANCED';
    const f = formations.get(id);
    const items: string[] = [
      `<span class="bl-stat">${t('battle.strip.power')} <b class="tabular-nums">${fmtInt(summary.attackerPower)}</b>${f ? buffHtml(signedPct(f.attackFactor)) : ''}</span>`,
      `<span class="bl-stat">${t('battle.strip.march')} <b class="tabular-nums">${formatMarchTime(row.marchSeconds)}</b>${f ? buffHtml(signedPct(f.marchTimeFactor)) : ''}</span>`,
    ];
    if (f && f.carryFactor !== 1) items.push(`<span class="bl-stat">${t('battle.strip.carry')}${buffHtml(signedPct(f.carryFactor))}</span>`);
    const base = formations.get('BALANCED');
    if (f && base && f.lootShare !== base.lootShare) {
      items.push(`<span class="bl-stat">${t('battle.strip.loot')} <b class="tabular-nums">${Math.round(f.lootShare * 100)}%</b>${buffHtml(signedPct(f.lootShare / base.lootShare))}</span>`);
    }
    return `
      <div class="bl-strip" data-battle-strip>
        <span class="bl-strip-name">${formationName(id)}</span>
        ${items.join('')}
      </div>`;
  }

  function rowResolvedHtml(row: BattleHistoryEntry, i: number): string {
    const summary = row.resultSummary as BattleResultSummary;
    const side = viewerSide(row, myColonyId);
    const expanded = expandedIds.has(row.id);
    const chevron = `<span class="bl-chev" aria-hidden="true">${expanded ? '&#9662;' : '&#9656;'}</span>`;
    const vs = `
      <div class="bl-vs truncate">
        ${combatantLabel(row.attackerColonyName, row.attackerUsername, row.attackerTitle)}
        <span class="text-text-dim">${t('battle.vs')}</span>
        ${combatantLabel(row.defenderColonyName, row.defenderUsername, row.defenderTitle)}
      </div>`;

    if (summary.isRecon) {
      // Recon never fights, never plunders — a plain WIN/LOSS casualty table
      // would be misleading. The defender only learns it was recon from this
      // label; the ATTACK_INCOMING warning they saw earlier never said so.
      const tier = summary.intel?.tier ?? 'NONE';
      const sent = side === 'attacker' ? summary.attackerLosses : {};
      const lost = sumQty(sent);
      const detailHtml = expanded
        ? `
          <div class="bl-detail">
            <div class="bl-recon-chips">
              <span class="bl-rchip" title="${formatRoute(row)}"><span aria-hidden="true">${icon('map')}</span>${formatRoute(row)}</span>
            </div>
            ${side === 'attacker' && lost > 0 ? `<div class="sr-only">${t('battle.reconLost', { n: lost })}</div>` : ''}
            ${side === 'attacker' ? renderIntel(summary.intel) : ''}
          </div>
        `
        : '';
      return `
        <div class="bl-card bl-card--recon${enterClass(row.id)}" ${rowStyle(i)} data-battle-row="${row.id}" data-battle-status="${row.status}">
          <button type="button" data-action="toggle-battle-row" data-battle-id="${row.id}" aria-expanded="${expanded}" class="bl-head touch-manipulation">
            <span class="bl-art" ${bannerStyle(BANNER_ART.recon)} aria-hidden="true"></span>
            <span class="bl-body">
              <span class="flex items-center justify-between gap-2 flex-wrap">
                <span class="bl-verdict bl-verdict--recon">${chevron}<span>${t('battle.recon', { tier: intelTierLabel(tier) })}</span></span>
                ${intelMeterHtml(tier)}
              </span>
              ${vs}
              ${reconTilesHtml(summary, side)}
            </span>
          </button>
          ${detailHtml}
        </div>
      `;
    }

    const framing = frameResolved(summary, side);
    const captureNote = summary.captured
      ? `<div class="text-[11px] text-gold-bright font-bold">${t('battle.colonyClaimed')}</div>`
      : summary.captureBlockedByColonyCap
        ? `<div class="text-[11px] text-crimson">${t('battle.captureBlocked')}</div>`
        : '';
    const detailHtml = expanded
      ? `
        <div class="bl-detail">
          <div class="text-text-dim text-[11px]">${formatRoute(row)}</div>
          ${formationStripHtml(row, summary, side)}
          ${renderBattleColumns(summary, side)}
          ${side === 'attacker' && summary.attackerWins && summary.plunderCapped ? `<div class="text-[11px] text-text-dim">${t('battle.carryLimited')}</div>` : ''}
          ${captureNote}
          ${side === 'attacker' ? renderIntel(summary.intel) : ''}
        </div>
      `
      : '';
    const won = framing.won;
    return `
      <div class="bl-card ${won ? 'bl-card--win' : 'bl-card--loss'}${enterClass(row.id)}" ${rowStyle(i)} data-battle-row="${row.id}" data-battle-status="${row.status}">
        <button type="button" data-action="toggle-battle-row" data-battle-id="${row.id}" aria-expanded="${expanded}" class="bl-head touch-manipulation">
          <span class="bl-art" ${bannerStyle(won ? BANNER_ART.win : BANNER_ART.loss)} aria-hidden="true"></span>
          <span class="bl-body">
            <span class="flex items-center justify-between gap-2 flex-wrap">
              <span class="bl-verdict ${won ? 'bl-verdict--win' : 'bl-verdict--loss'}">${chevron}<span class="bl-verdict-word" data-text="${won ? t('battle.win') : t('battle.loss')}">${won ? t('battle.win') : t('battle.loss')}</span>${formationChipHtml(summary.formation ?? row.formation)}</span>
              <span class="bl-sub">${formatDate(row.startedAt)}</span>
            </span>
            ${vs}
            ${statColumnsHtml(framing, summary, side)}
          </span>
        </button>
        ${detailHtml}
      </div>
    `;
  }

  function rowCancelledHtml(row: BattleHistoryEntry, i: number): string {
    const side = viewerSide(row, myColonyId);
    const other = side === 'attacker'
      ? combatantLabel(row.defenderColonyName, row.defenderUsername, row.defenderTitle)
      : combatantLabel(row.attackerColonyName, row.attackerUsername, row.attackerTitle);
    return `
      <div class="bl-card bl-card--cancelled${enterClass(row.id)}" ${rowStyle(i)} data-battle-row="${row.id}" data-battle-status="CANCELLED">
        <div class="bl-body">
          <div class="flex items-center justify-between gap-2 flex-wrap">
            <span class="text-text-dim font-bold text-xs">${t('battle.cancelled')}</span>
            <span class="bl-sub">${formatDate(row.startedAt)}</span>
          </div>
          <div class="bl-sub">${t(side === 'attacker' ? 'battle.recalledMarch' : 'battle.calledOff', { name: other })}</div>
        </div>
      </div>
    `;
  }

  function renderAlerts(): void {
    // An alert whose march has landed is over: it must leave the log at 00:00, not rest there. (The server also
    // stops returning it once the battle resolves, but resolution is lazy, so this clock check is what clears it.)
    const nowMs = Date.now();
    const incoming = alerts.filter((a) => a.kind === 'ATTACK_INCOMING' && new Date((a.payload as AttackIncomingPayload).arrivalAt).getTime() > nowMs);
    alertsSectionEl.classList.toggle('hidden', incoming.length === 0);
    if (incoming.length === 0) {
      alertsListEl.innerHTML = '';
      return;
    }
    alertsListEl.innerHTML = incoming
      .map((a) => {
        const p = a.payload as AttackIncomingPayload;
        const remaining = new Date(p.arrivalAt).getTime() - Date.now();
        return `
          <div class="bl-alert" data-alert-id="${a.id}">
            <span class="bl-art" ${bannerStyle(BANNER_ART.incoming)} aria-hidden="true"></span>
            <span class="bl-alert-body">
              <span class="bl-alert-title">${t('battle.alertIncoming', { name: p.targetColonyName })}</span>
              <span class="bl-clock tabular-nums" data-alert-countdown="${a.id}">${formatCountdown(Math.max(0, remaining))}</span>
            </span>
          </div>
        `;
      })
      .join('');
  }

  async function refreshAlerts(): Promise<void> {
    try {
      const feed = await fetchNotifications();
      if (disposed) return;
      alerts = feed.items;
      renderAlerts();
      // Auto-mark-read on open — matches what opening the Battle tab means
      // to a player (they have seen these). Use the response's own
      // unreadCount so the tab badge clears immediately rather than waiting
      // up to 15s for the next background poll.
      if (feed.unreadCount > 0) {
        const unreadIds = feed.items.filter((n) => !n.readAt).map((n) => n.id);
        const result = await markNotificationsRead(unreadIds);
        if (disposed) return;
        setTabBadge('battle', result.unreadCount);
      }
    } catch {
      // Non-critical — the next 15s poll will retry.
    }
  }

  function renderHistory(): void {
    if (history.length === 0) {
      historyListEl.innerHTML = `<div class="text-text-dim">${t('battle.empty')}</div>`;
      return;
    }
    historyListEl.innerHTML = history
      .map((row, i) => {
        if (row.status === 'PENDING') return rowPendingHtml(row, i);
        if (row.status === 'CANCELLED') return rowCancelledHtml(row, i);
        // RETURNING: combat is decided and resultSummary is populated —
        // survivors are just still walking home. Rendered exactly like RESOLVED.
        return rowResolvedHtml(row, i);
      })
      .join('');
    // Only rows that just appeared count up their numbers; then they are "seen" for every later re-render.
    historyListEl.querySelectorAll<HTMLElement>('.bl-enter').forEach((card) => animateCounts(card));
    for (const row of history) seenRows.add(row.id);
  }

  function tickCountdowns(): void {
    if (disposed) return;
    let anyArrived = false;
    const landed = new Set<string>();
    for (const a of alerts) {
      if (a.kind !== 'ATTACK_INCOMING') continue;
      const p = a.payload as AttackIncomingPayload;
      const remaining = new Date(p.arrivalAt).getTime() - Date.now();
      if (remaining <= 0) {
        landed.add(a.id);
        continue;
      }
      const el = alertsListEl.querySelector(`[data-alert-countdown="${a.id}"]`);
      if (el) el.textContent = formatCountdown(remaining);
    }
    if (landed.size > 0) {
      // Drop the landed alert(s) straight away (and stop re-triggering on them every tick), then refetch so the
      // resolved battle shows up in the log.
      alerts = alerts.filter((a) => !landed.has(a.id));
      renderAlerts();
      anyArrived = true;
    }
    for (const row of history) {
      if (row.status !== 'PENDING') continue;
      const remaining = new Date(row.arrivalAt).getTime() - Date.now();
      const bar = historyListEl.querySelector(`[data-battle-progress="${row.id}"]`) as HTMLElement | null;
      if (bar) bar.style.width = `${marchProgressPct(row)}%`;
      const el = historyListEl.querySelector(`[data-battle-countdown="${row.id}"]`);
      if (el) el.textContent = formatCountdown(remaining);
      const cancelBtn = historyListEl.querySelector(`[data-action="cancel-battle"][data-battle-id="${row.id}"]`) as HTMLButtonElement | null;
      if (cancelBtn) {
        const cancellable = canCancelMarch(row.startedAt, row.arrivalAt);
        cancelBtn.disabled = !cancellable;
        cancelBtn.hidden = !cancellable;
      }
      if (remaining <= 0) anyArrived = true;
    }
    if (anyArrived) {
      const now = Date.now();
      if (!refetchInFlight && now - lastRefetchAt >= REFETCH_COOLDOWN_MS) {
        lastRefetchAt = now;
        void refreshHistory();
        void refreshAlerts();
      }
    }
  }

  async function refreshHistory(): Promise<void> {
    if (refetchInFlight) return;
    refetchInFlight = true;
    try {
      const fetched = await fetchBattleHistory();
      if (disposed) return;
      history = fetched;
      renderHistory();
    } catch (err) {
      if (!disposed) showToast(err instanceof Error ? err.message : t('battle.loadFailed'), 'error');
    } finally {
      refetchInFlight = false;
    }
  }

  let cancelling = false;

  async function handleCancelBattle(battleId: string): Promise<void> {
    if (cancelling) return;
    cancelling = true;
    try {
      await cancelBattle(battleId);
      if (disposed) return;
      showToast(t('battle.cancelledToast'), 'success');
      window.dispatchEvent(new Event('marches:refresh'));
      await refreshHistory();
    } catch (err) {
      if (!disposed) showToast(err instanceof Error ? err.message : t('battle.cancelFailed'), 'error');
    } finally {
      cancelling = false;
    }
  }

  function onHistoryClick(e: MouseEvent): void {
    const target = e.target as HTMLElement;
    const actionEl = target.closest('[data-action]') as HTMLElement | null;
    if (!actionEl) return;
    const action = actionEl.dataset.action;
    const id = actionEl.dataset.battleId;
    if (!id) return;
    if (action === 'toggle-battle-row') {
      if (expandedIds.has(id)) expandedIds.delete(id);
      else expandedIds.add(id);
      renderHistory();
    } else if (action === 'cancel-battle') {
      void handleCancelBattle(id);
    }
  }
  historyListEl.addEventListener('click', onHistoryClick);

  async function handleMarkAllRead(): Promise<void> {
    try {
      const result = await markNotificationsRead();
      if (disposed) return;
      setTabBadge('battle', result.unreadCount);
      await refreshAlerts();
    } catch (err) {
      if (!disposed) showToast(err instanceof Error ? err.message : t('battle.markReadFailed'), 'error');
    }
  }

  function onAlertsClick(e: MouseEvent): void {
    const target = e.target as HTMLElement;
    if (target.closest('[data-action="mark-all-read"]')) void handleMarkAllRead();
  }
  alertsSectionEl.addEventListener('click', onAlertsClick);

  async function init(): Promise<void> {
    try {
      myColonyId = await fetchMyColonyId();
    } catch {
      myColonyId = null;
    }
    if (disposed) return;
    try {
      for (const f of await fetchFormations()) formations.set(f.id, f);
    } catch {
      // Without the table the log shows formation names but no bonus parentheses.
    }
    if (disposed) return;
    await Promise.all([refreshHistory(), refreshAlerts()]);
    if (disposed) return;
    countdownTimer = setInterval(tickCountdowns, COUNTDOWN_TICK_MS);
  }

  void init();

  return () => {
    disposed = true;
    if (countdownTimer !== null) clearInterval(countdownTimer);
    historyListEl.removeEventListener('click', onHistoryClick);
    alertsSectionEl.removeEventListener('click', onAlertsClick);
  };
}

export const battleTab: GameTab = {
  id: 'battle',
  label: t('nav.battle'),
  icon: 'battle',
  mount: renderBattleScreen,
};
